// src/pages/ClientList.js - VERSION COMPLÈTE AVEC LIEN VERS PAGE D'IMPORT
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import * as XLSX from "xlsx";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Palette de couleurs SecureControl
const COLORS = {
  primary: '#0B3C5D',     // Bleu Sécurité
  secondary: '#1E88C8',   // Bleu Tech
  accent: '#4FC3F7',      // Bleu Clair
  darkText: '#2E2E2E',    // Gris Foncé
  lightBg: '#F5F7FA',     // Gris Clair
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3',
  successLight: '#e8f5e9',
  dangerLight: '#ffebee',
  warningLight: '#fff3e0',
  infoLight: '#e3f2fd'
};

// Options pour les filtres
const TYPE_CLIENT_OPTIONS = [
  { value: "", label: "Tous les types" },
  { value: "1", label: "Entreprise" },
  { value: "2", label: "Particulier" }
];

const STATUT_CLIENT_OPTIONS = [
  { value: "", label: "Tous les statuts" },
  { value: "0", label: "Inactif" },
  { value: "1", label: "Actif" },
  { value: "2", label: "Suspendu" }
];

// Fonction pour le label de type client
const typeClientLabel = (typeValue) => {
  switch (String(typeValue)) {
    case "1": return "Entreprise";
    case "2": return "Particulier";
    default: return "Inconnu";
  }
};

// Fonction pour le label et couleur du statut
const statutClientLabel = (statut) => {
  switch (statut) {
    case 1: return { text: "Actif", className: "badge bg-success", icon: "bi-check-circle" };
    case 0: return { text: "Inactif", className: "badge bg-secondary", icon: "bi-x-circle" };
    case 2: return { text: "Suspendu", className: "badge bg-warning text-dark", icon: "bi-pause-circle" };
    default: return { text: "Inconnu", className: "badge bg-light text-dark", icon: "bi-question-circle" };
  }
};

// Fonction pour la couleur de l'état du contrat
const getContratStyle = (etat, typeJours) => {
  switch (etat) {
    case "vert": return { 
      backgroundColor: '#e8f5e9', 
      color: '#2e7d32',
      borderColor: '#4caf50'
    };
    case "orange": return { 
      backgroundColor: '#fff3e0', 
      color: '#ef6c00',
      borderColor: '#ff9800'
    };
    case "rouge": return { 
      backgroundColor: '#ffebee', 
      color: '#c62828',
      borderColor: '#f44336'
    };
    case "expire": return { 
      backgroundColor: '#f5f5f5', 
      color: '#757575',
      borderColor: '#9e9e9e'
    };
    default: return { 
      backgroundColor: '#f5f5f5', 
      color: '#757575',
      borderColor: '#9e9e9e'
    };
  }
};

// Fonction utilitaire pour valider les valeurs
const validateValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return String(value).trim();
};

const ClientList = () => {
  const navigate = useNavigate();
  
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serverError, setServerError] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportType, setReportType] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatut, setFilterStatut] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Récupérer les infos utilisateur
  const [userInfo, setUserInfo] = useState(() => {
    try {
      const stored = localStorage.getItem("user");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // Permission
  const userProfile = userInfo ? String(userInfo.profile || userInfo.poste || "") : "";
  const isGestionnaire = userProfile === "3";
  const canAccess = userProfile === "2" || userProfile === "3";

  useEffect(() => {
    if (userInfo && userInfo.societe_id) {
      fetchData();
    }
  }, [userInfo]);

  // Appliquer les filtres
  useEffect(() => {
    if (clients.length > 0) {
      let filtered = [...clients];

      // Filtre par recherche
      if (searchTerm.trim() !== "") {
        const term = searchTerm.toLowerCase();
        filtered = filtered.filter(client =>
          (client.nom_client && client.nom_client.toLowerCase().includes(term)) ||
          (client.prenom_client && client.prenom_client.toLowerCase().includes(term)) ||
          (client.email_client && client.email_client.toLowerCase().includes(term)) ||
          (client.tel_client && client.tel_client.toLowerCase().includes(term)) ||
          (client.nif_client && client.nif_client.toLowerCase().includes(term)) ||
          (client.rccm_client && client.rccm_client.toLowerCase().includes(term))
        );
      }

      // Filtre par type
      if (filterType) {
        filtered = filtered.filter(client => String(client.type_client) === filterType);
      }

      // Filtre par statut
      if (filterStatut) {
        filtered = filtered.filter(client => String(client.statut_client) === filterStatut);
      }

      setFilteredClients(filtered);
      setCurrentPage(1);
      
      if (filtered.length === 0 && (searchTerm || filterType || filterStatut)) {
        setInfoMsg("Aucun client ne correspond aux critères de recherche.");
      } else {
        setInfoMsg("");
      }
    } else {
      setFilteredClients([]);
    }
  }, [clients, searchTerm, filterType, filterStatut]);

  const fetchData = async () => {
    if (!userInfo) return;
    
    setLoading(true);
    setServerError("");
    
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }
      
      // limit=2000 : cette page filtre/pagine côté client sur la liste
      // complète — sans ce paramètre, le backend ne renvoyait que les 20
      // premiers clients par défaut, rendant les suivants invisibles.
      const url = `${API_BASE_URL}/clients/list.php?limit=2000`;
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      
      if (response.data?.success) {
        const allClients = response.data.data?.clients || [];
        const activeClients = allClients.filter(client => 
          !client.date_supprimer_client || 
          client.date_supprimer_client === "0000-00-00 00:00:00" ||
          client.date_supprimer_client === ""
        );
        
        setClients(activeClients);
        setFilteredClients(activeClients);
        
        if (activeClients.length === 0) {
          setInfoMsg("Aucun client trouvé dans la base de données.");
        }
      } else {
        setServerError(response.data?.message || "Impossible de charger la liste des clients.");
      }
    } catch (err) {
      handleFetchError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchError = (err) => {
    console.error("Fetch error:", err);
    if (err.response?.status === 401) {
      setServerError("Session expirée. Veuillez vous reconnecter.");
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
      setTimeout(() => navigate("/login"), 2000);
    } else if (err.response?.status === 403) {
      setServerError("Accès interdit. Vous n'avez pas les permissions nécessaires.");
    } else if (err.response?.status === 500) {
      setServerError("Erreur serveur. Veuillez contacter l'administrateur.");
    } else if (err.request) {
      setServerError("Serveur inaccessible. Vérifiez votre connexion.");
    } else {
      setServerError("Erreur technique lors du chargement des données.");
    }
  };

  const handleViewDetails = (client) => {
    navigate(`/clients/details/${client.id_client}`);
  };

  const handleCreateClient = () => {
    navigate("/clients/create");
  };

  const handleHistoriqueRappels = () => {
    navigate("/historique-rappels");
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "0000-00-00" || dateString === "0000-00-00 00:00:00") {
      return "-";
    }
    try {
      return new Date(dateString).toLocaleDateString('fr-FR');
    } catch (error) {
      return dateString;
    }
  };

  // Générer un CSV
  const generateCSV = () => {
    setGeneratingReport(true);
    setReportType("csv");
    
    try {
      const headers = [
        "ID Client",
        "Nom", 
        "Prénom",
        "Type Client",
        "Email",
        "Téléphone",
        "Adresse",
        "NIF",
        "RCCM",
        "Statut",
        "Date Début Contrat",
        "Date Fin Contrat",
        "État Contrat",
        "Message Contrat",
        "Jours Restants",
        "Date Création"
      ];
      
      const csvData = clients.map(client => [
        client.id_client,
        `"${client.nom_client || ''}"`,
        `"${client.prenom_client || ''}"`,
        typeClientLabel(client.type_client),
        `"${client.email_client || ''}"`,
        `"${client.tel_client || ''}"`,
        `"${client.addresse_client || ''}"`,
        `"${client.nif_client || ''}"`,
        `"${client.rccm_client || ''}"`,
        statutClientLabel(client.statut_client).text,
        `"${formatDate(client.date_debut_contrat_client)}"`,
        `"${formatDate(client.date_fin_contrat_client)}"`,
        client.etat_contrat || "Inconnu",
        `"${client.message_contrat || ''}"`,
        client.jours_contrat || "0",
        `"${formatDate(client.created_at)}"`
      ]);
      
      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      
      link.setAttribute("href", url);
      link.setAttribute("download", `clients_securecontrol_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error("Erreur génération CSV:", error);
      setServerError("Erreur lors de la génération du CSV: " + error.message);
    } finally {
      setGeneratingReport(false);
      setReportType("");
    }
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredClients.length > 0 ? filteredClients.slice(indexOfFirstItem, indexOfLastItem) : [];
  const totalPages = Math.max(1, Math.ceil(filteredClients.length / itemsPerPage));

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const resetSearch = () => {
    setSearchTerm("");
  };

  const resetFilters = () => {
    setSearchTerm("");
    setFilterType("");
    setFilterStatut("");
  };

  const hasActiveFilters = () => {
    return searchTerm || filterType || filterStatut;
  };

  // Si pas d'utilisateur
  if (!userInfo) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion des clients</h1>
        </div>
        <section className="section">
          <div className="text-center py-5">
            <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
              <span className="visually-hidden">Chargement...</span>
            </div>
            <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement des informations...</p>
          </div>
        </section>
      </Layout>
    );
  }

  // Si pas accès
  if (!canAccess) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion des clients</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Clients</li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="text-center py-5">
            <div className="mb-4">
              <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                width: '80px',
                height: '80px',
                backgroundColor: '#F5F7FA',
                color: '#B0BEC5'
              }}>
                <i className="bi bi-lock fs-1"></i>
              </div>
            </div>
            <h5 className="mb-3" style={{ color: COLORS.primary, fontWeight: '600' }}>Accès restreint</h5>
            <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
              Seuls les gestionnaires et contrôleurs peuvent accéder à cette page.
            </p>
            <button
              className="btn fw-semibold mt-4"
              onClick={() => navigate("/dashboard")}
              style={{
                color: COLORS.primary,
                borderColor: COLORS.primary,
                backgroundColor: 'transparent',
                borderRadius: '8px',
                padding: '10px 24px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = COLORS.primary;
                e.target.style.color = COLORS.white;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = COLORS.primary;
              }}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Retour au tableau de bord
            </button>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion des clients</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Clients</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5 className="card-title mb-1" style={{ 
                  color: COLORS.primary,
                  borderBottom: `2px solid ${COLORS.lightBg}`,
                  paddingBottom: '16px',
                  width: '100%'
                }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                      width: '36px',
                      height: '36px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-people"></i>
                    </div>
                    Liste des clients
                  </div>
                </h5>
                <p className="text-muted mb-0" style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                  
                </p>
              </div>
              {/* Section des boutons */}
              <div className="d-flex gap-2 align-items-stretch">
                {hasActiveFilters() && (
                  <button
                    className="btn fw-semibold d-flex align-items-center"
                    onClick={resetFilters}
                    title="Réinitialiser tous les filtres"
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '10px 20px',
                      transition: 'all 0.3s ease',
                      height: '48px',
                      alignItems: 'center',
                      display: 'flex'
                    }}
                    data-bs-toggle="tooltip"
                    data-bs-placement="bottom"
                    data-bs-title="Réinitialiser tous les filtres"
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Réinitialiser
                  </button>
                )}
              
                <button
                  onClick={handleHistoriqueRappels}
                  className="btn fw-semibold d-flex align-items-center"
                  style={{
                    color: COLORS.info,
                    borderColor: COLORS.info,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    transition: 'all 0.3s ease',
                    height: '48px',
                    alignItems: 'center',
                    display: 'flex'
                  }}
                  data-bs-toggle="tooltip"
                  data-bs-placement="bottom"
                  data-bs-title="Voir l'historique des rappels"
                >
                  <i className="bi bi-envelope-check me-2"></i>
                  Historique Rappels
                </button>

                {isGestionnaire && (
                  <Link
                    to="/client-import"
                    className="btn fw-semibold d-flex align-items-center"
                    style={{
                      color: COLORS.white,
                      backgroundColor: COLORS.success,
                      borderColor: COLORS.success,
                      borderRadius: '8px',
                      padding: '10px 24px',
                      height: '48px',
                      alignItems: 'center',
                      display: 'flex',
                      textDecoration: 'none'
                    }}
                    data-bs-toggle="tooltip"
                    data-bs-placement="bottom"
                    data-bs-title="Importer des clients en masse"
                  >
                    <i className="bi bi-file-earmark-excel me-2"></i>
                    Importer Excel
                  </Link>
                )}

                {isGestionnaire && (
                  <button
                    onClick={handleCreateClient}
                    className="btn fw-semibold d-flex align-items-center"
                    style={{
                      color: COLORS.white,
                      backgroundColor: COLORS.primary,
                      borderColor: COLORS.primary,
                      borderRadius: '8px',
                      padding: '10px 24px',
                      height: '48px',
                      alignItems: 'center',
                      display: 'flex'
                    }}
                    data-bs-toggle="tooltip"
                    data-bs-placement="bottom"
                    data-bs-title="Créer un nouveau client"
                  >
                    <i className="bi bi-plus-lg me-2"></i>
                    Nouveau client
                  </button>
                )}

                <div className="dropdown d-flex align-items-stretch">
                  <button
                    className="btn fw-semibold d-flex align-items-center"
                    type="button"
                    data-bs-toggle="dropdown"
                    disabled={generatingReport || clients.length === 0}
                    style={{
                      color: COLORS.white,
                      backgroundColor: COLORS.warning,
                      borderColor: COLORS.warning,
                      borderRadius: '8px',
                      padding: '10px 20px',
                      transition: 'all 0.3s ease',
                      height: '48px',
                      alignItems: 'center',
                      display: 'flex'
                    }}
                  >
                    {generatingReport ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        {reportType === "csv" ? "CSV..." : "Génération..."}
                      </>
                    ) : (
                      <>
                        <i className="bi bi-download me-2"></i>
                        Exporter
                      </>
                    )}
                  </button>
                  <ul className="dropdown-menu" style={{ 
                    borderRadius: '8px',
                    border: `1px solid ${COLORS.lightBg}`,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}>
                    <li>
                      <button 
                        className="dropdown-item" 
                        onClick={generateCSV}
                        disabled={generatingReport}
                        style={{
                          color: COLORS.darkText,
                          padding: '10px 16px',
                          display: 'flex',
                          alignItems: 'center',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = COLORS.lightBg;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                        }}
                      >
                        <i className="bi bi-file-earmark-excel me-2" style={{ color: COLORS.success }}></i>
                        Exporter en CSV
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {serverError && (
              <div className="alert alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.danger}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{serverError}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setServerError("")}
                  ></button>
                </div>
              </div>
            )}
            
            {infoMsg && !loading && (
              <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#e8f5e9',
                borderColor: COLORS.success,
                color: '#2e7d32',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.success}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-info-circle me-2"></i>
                  <div className="flex-grow-1">
                    {infoMsg.split('\n').map((line, i) => (
                      <div key={i}>{line}</div>
                    ))}
                  </div>
                  <button type="button" className="btn-close" onClick={() => setInfoMsg("")}></button>
                </div>
              </div>
            )}

            {/* Filtres */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card" style={{ 
                  backgroundColor: '#E3F2FD',
                  border: 'none',
                  borderRadius: '8px'
                }}>
                  <div className="card-body py-3">
                    <div className="row g-3">
                      <div className="col-md-4">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-search"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Rechercher (nom, prénom, email, téléphone, NIF, RCCM)..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                            data-bs-toggle="tooltip"
                            data-bs-placement="bottom"
                            data-bs-title="Rechercher par nom, prénom, email, téléphone, NIF ou RCCM"
                          />
                          {searchTerm && (
                            <button
                              className="btn"
                              type="button"
                              onClick={resetSearch}
                              title="Réinitialiser la recherche"
                              style={{
                                color: COLORS.primary,
                                borderColor: '#e0e0e0',
                                backgroundColor: COLORS.white,
                                borderLeft: 'none'
                              }}
                              data-bs-toggle="tooltip"
                              data-bs-placement="bottom"
                              data-bs-title="Effacer la recherche"
                            >
                              <i className="bi bi-x"></i>
                            </button>
                          )}
                        </div>
                      </div>
                      
                      <div className="col-md-4">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-tag"></i>
                          </span>
                          <select 
                            className="form-select"
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                            data-bs-toggle="tooltip"
                            data-bs-placement="bottom"
                            data-bs-title="Filtrer par type de client"
                          >
                            <option value="">Tous les types</option>
                            <option value="1">Entreprise</option>
                            <option value="2">Particulier</option>
                          </select>
                        </div>
                      </div>

                      <div className="col-md-4">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-funnel"></i>
                          </span>
                          <select 
                            className="form-select"
                            value={filterStatut}
                            onChange={(e) => setFilterStatut(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                            data-bs-toggle="tooltip"
                            data-bs-placement="bottom"
                            data-bs-title="Filtrer par statut"
                          >
                            <option value="">Tous les statuts</option>
                            <option value="1">Actif</option>
                            <option value="0">Inactif</option>
                            <option value="2">Suspendu</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 d-flex justify-content-between align-items-center">
                      <div className="text-muted small" style={{ color: COLORS.darkText }}>
                        <i className="bi bi-info-circle me-1"></i>
                        <span className="badge fw-semibold me-2" style={{ 
                          backgroundColor: COLORS.lightBg,
                          color: COLORS.primary,
                          padding: '4px 12px',
                          borderRadius: '6px'
                        }}>
                          {filteredClients.length} client(s)
                          {filteredClients.length !== clients.length && clients.length > 0 && 
                            ` sur ${clients.length} au total`}
                        </span>
                        
                        {hasActiveFilters() && (
                          <span>
                            <i className="bi bi-funnel me-1"></i>
                            Filtres actifs:
                            {searchTerm && <span className="badge bg-info ms-1" style={{ backgroundColor: COLORS.info }}>Recherche</span>}
                            {filterType && <span className="badge bg-info ms-1" style={{ backgroundColor: COLORS.info }}>Type</span>}
                            {filterStatut && <span className="badge bg-info ms-1" style={{ backgroundColor: COLORS.info }}>Statut</span>}
                          </span>
                        )}
                      </div>
                      
                      <button
                        onClick={fetchData}
                        className="btn fw-semibold"
                        disabled={loading}
                        style={{
                          color: COLORS.primary,
                          borderColor: COLORS.primary,
                          backgroundColor: 'transparent',
                          borderRadius: '8px',
                          padding: '6px 16px',
                          fontSize: '0.85rem'
                        }}
                        data-bs-toggle="tooltip"
                        data-bs-placement="bottom"
                        data-bs-title="Actualiser la liste des clients"
                      >
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Actualiser
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement des clients...</p>
              </div>
            ) : clients.length === 0 ? (
              <div className="text-center py-5">
                <div className="mb-4">
                  <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                    width: '80px',
                    height: '80px',
                    backgroundColor: '#F5F7FA',
                    color: '#B0BEC5'
                  }}>
                    <i className="bi bi-people fs-1"></i>
                  </div>
                </div>
                <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucun client trouvé</h6>
                <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                  Aucun client n'a été enregistré pour le moment.
                </p>
                {isGestionnaire && (
                  <button
                    onClick={handleCreateClient}
                    className="btn fw-semibold mt-3"
                    style={{
                      color: COLORS.white,
                      backgroundColor: COLORS.primary,
                      borderColor: COLORS.primary,
                      borderRadius: '8px',
                      padding: '10px 24px'
                    }}
                    data-bs-toggle="tooltip"
                    data-bs-placement="bottom"
                    data-bs-title="Créer un nouveau client"
                  >
                    <i className="bi bi-plus-lg me-2"></i>
                    Créer un client
                  </button>
                )}
              </div>
            ) : (
              <>
                {filteredClients.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="mb-4">
                      <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                        width: '80px',
                        height: '80px',
                        backgroundColor: '#F5F7FA',
                        color: '#B0BEC5'
                      }}>
                        <i className="bi bi-search fs-1"></i>
                      </div>
                    </div>
                    <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucun résultat</h6>
                    <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                      Aucun client ne correspond aux critères de recherche.
                    </p>
                    <button
                      className="btn fw-semibold mt-3"
                      onClick={resetFilters}
                      style={{
                        color: COLORS.primary,
                        borderColor: COLORS.primary,
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        padding: '10px 24px'
                      }}
                      data-bs-toggle="tooltip"
                      data-bs-placement="bottom"
                      data-bs-title="Effacer tous les filtres"
                    >
                      <i className="bi bi-x-lg me-2"></i>
                      Effacer les filtres
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead>
                          <tr style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderBottom: `2px solid ${COLORS.accent}`
                          }}>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Client</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Contact</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Type</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Contrat</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Durée contrat</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Statut</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Créé le</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px', textAlign: 'center' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentItems.map((client, index) => {
                            const statut = statutClientLabel(client.statut_client);
                            const contratStyle = getContratStyle(client.etat_contrat, client.type_jours);

                            return (
                              <tr key={client.id_client || `row-${index}`} style={{ borderBottom: `1px solid ${COLORS.lightBg}` }}>
                                
                                <td style={{ padding: '16px' }}>
                                  <div className="d-flex align-items-center">
                                    <div className="me-3">
                                      <div 
                                        className="text-white rounded-circle d-flex align-items-center justify-content-center fw-semibold"
                                        style={{ 
                                          width: '44px', 
                                          height: '44px', 
                                          fontSize: '15px',
                                          backgroundColor: COLORS.primary,
                                          boxShadow: '0 2px 4px rgba(11, 60, 93, 0.1)'
                                        }}
                                        title="Client"
                                      >
                                        {client.nom_client?.charAt(0) || ''}{client.prenom_client?.charAt(0) || ''}
                                      </div>
                                    </div>
                                    <div>
                                      <div style={{ color: COLORS.primary, fontWeight: '500', fontSize: '0.95rem' }}>
                                        {client.nom_client} {client.prenom_client}
                                      </div>
                                      <div className="small" style={{ color: '#6c757d', fontSize: '0.8rem' }}>
                                        {client.nif_client ? `NIF: ${client.nif_client}` : ''}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                
                                <td style={{ padding: '16px' }}>
                                  <div>
                                    <div className="d-flex align-items-center mb-1">
                                      <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                                        width: '20px',
                                        height: '20px',
                                        backgroundColor: '#E3F2FD',
                                        color: COLORS.secondary,
                                        fontSize: '0.7rem'
                                      }}>
                                        <i className="bi bi-envelope"></i>
                                      </div>
                                      <small style={{ color: COLORS.darkText }}>{client.email_client || '-'}</small>
                                    </div>
                                    <div className="d-flex align-items-center">
                                      <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                                        width: '20px',
                                        height: '20px',
                                        backgroundColor: '#E3F2FD',
                                        color: COLORS.secondary,
                                        fontSize: '0.7rem'
                                      }}>
                                        <i className="bi bi-telephone"></i>
                                      </div>
                                      <small style={{ color: COLORS.darkText }}>{client.tel_client || '-'}</small>
                                    </div>
                                  </div>
                                </td>
                                
                                <td style={{ padding: '16px' }}>
                                  <span className="badge fw-semibold" style={{ 
                                    backgroundColor: '#E3F2FD',
                                    color: COLORS.primary,
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    fontSize: '0.8rem'
                                  }}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="bottom"
                                  data-bs-title={typeClientLabel(client.type_client)}
                                  >
                                    {typeClientLabel(client.type_client)}
                                  </span>
                                </td>
                                
                                <td style={{ padding: '16px' }}>
                                  <div className="small">
                                    <div className="d-flex align-items-center mb-1">
                                      <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                                        width: '16px',
                                        height: '16px',
                                        backgroundColor: '#E3F2FD',
                                        color: COLORS.secondary,
                                        fontSize: '0.6rem'
                                      }}>
                                        <i className="bi bi-calendar-start"></i>
                                      </div>
                                      <span style={{ color: COLORS.darkText }}>{formatDate(client.date_debut_contrat_client)}</span>
                                    </div>
                                    <div className="d-flex align-items-center">
                                      <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                                        width: '16px',
                                        height: '16px',
                                        backgroundColor: '#E3F2FD',
                                        color: COLORS.secondary,
                                        fontSize: '0.6rem'
                                      }}>
                                        <i className="bi bi-calendar-check"></i>
                                      </div>
                                      <span style={{ color: COLORS.darkText }}>{formatDate(client.date_fin_contrat_client)}</span>
                                    </div>
                                  </div>
                                </td>
                                
                                <td style={{ padding: '16px' }}>
                                  <div className="badge fw-semibold" style={{
                                    ...contratStyle,
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    fontSize: '0.8rem',
                                    border: `1px solid ${contratStyle.borderColor}`,
                                    display: 'inline-block'
                                  }}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="bottom"
                                  data-bs-title={`État du contrat: ${client.etat_contrat || 'Inconnu'}`}
                                  >
                                    {client.message_contrat || `${client.jours_contrat} jour(s)`}
                                  </div>
                                </td>
                                
                                <td style={{ padding: '16px' }}>
                                  <span className="badge fw-semibold" style={{ 
                                    backgroundColor: client.statut_client === 1 ? '#e8f5e9' : 
                                                   client.statut_client === 0 ? '#f5f5f5' : '#fff3e0',
                                    color: client.statut_client === 1 ? '#2e7d32' : 
                                           client.statut_client === 0 ? '#757575' : '#ef6c00',
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    fontSize: '0.8rem'
                                  }}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="bottom"
                                  data-bs-title={`Statut: ${statut.text}`}
                                  >
                                    <i className={`bi ${statut.icon} me-1`}></i>
                                    {statut.text}
                                  </span>
                                </td>
                                
                                <td style={{ padding: '16px' }}>
                                  <small className="text-muted" style={{ fontSize: '0.85rem' }}>
                                    {formatDate(client.created_at)}
                                  </small>
                                </td>
                                
                                <td style={{ padding: '16px', textAlign: 'center' }}>
                                  <div className="d-flex gap-2 justify-content-center">
                                    <button
                                      className="btn btn-outline-success fw-semibold"
                                      onClick={() => handleViewDetails(client)}
                                      title="Voir les détails"
                                      disabled={updatingId === client.id_client}
                                      style={{
                                        borderRadius: '6px',
                                        padding: '8px 16px',
                                        fontSize: '0.85rem',
                                        minWidth: '44px'
                                      }}
                                      data-bs-toggle="tooltip"
                                      data-bs-placement="bottom"
                                      data-bs-title="Voir les détails du client"
                                    >
                                      <i className="bi bi-eye"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-4">
                        <div className="d-flex align-items-center">
                          <span className="me-2" style={{ color: COLORS.darkText }}>Afficher :</span>
                          <select 
                            className="form-select form-select-sm w-auto"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText,
                              borderRadius: '6px'
                            }}
                            data-bs-toggle="tooltip"
                            data-bs-placement="bottom"
                            data-bs-title="Nombre de clients par page"
                          >
                            <option value="5">5</option>
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                          </select>
                          <span className="ms-3" style={{ color: COLORS.darkText }}>
                            <span className="badge fw-semibold me-2" style={{ 
                              backgroundColor: '#E3F2FD',
                              color: COLORS.primary,
                              padding: '4px 12px',
                              borderRadius: '6px'
                            }}>
                              {filteredClients.length} client(s)
                              {filteredClients.length !== clients.length && clients.length > 0 ? ' (filtrés)' : ''}
                            </span>
                          </span>
                        </div>
                        
                        <nav>
                          <ul className="pagination pagination-sm mb-0">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button 
                                className="page-link fw-semibold"
                                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                                style={{
                                  color: currentPage === 1 ? '#B0BEC5' : COLORS.primary,
                                  borderColor: '#e0e0e0',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  margin: '0 2px'
                                }}
                                data-bs-toggle="tooltip"
                                data-bs-placement="bottom"
                                data-bs-title="Page précédente"
                              >
                                <i className="bi bi-chevron-left"></i>
                              </button>
                            </li>
                            
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                              .filter(page => {
                                if (totalPages <= 7) return true;
                                return page === 1 || 
                                       page === totalPages || 
                                       (page >= currentPage - 1 && page <= currentPage + 1);
                              })
                              .map((page, index, array) => {
                                if (index > 0 && page > array[index - 1] + 1) {
                                  return (
                                    <React.Fragment key={`ellipsis-${page}`}>
                                      <li className="page-item disabled">
                                        <span className="page-link" style={{ borderColor: '#e0e0e0' }}>...</span>
                                      </li>
                                      <li className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                        <button 
                                          className="page-link fw-semibold"
                                          onClick={() => handlePageChange(page)}
                                          style={{
                                            backgroundColor: currentPage === page ? COLORS.primary : 'transparent',
                                            borderColor: currentPage === page ? COLORS.primary : '#e0e0e0',
                                            color: currentPage === page ? COLORS.white : COLORS.primary,
                                            padding: '6px 12px',
                                            borderRadius: '6px',
                                            margin: '0 2px'
                                          }}
                                          data-bs-toggle="tooltip"
                                          data-bs-placement="bottom"
                                          data-bs-title={`Page ${page}`}
                                        >
                                          {page}
                                        </button>
                                      </li>
                                    </React.Fragment>
                                  );
                                }
                                
                                return (
                                  <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                    <button 
                                      className="page-link fw-semibold"
                                      onClick={() => handlePageChange(page)}
                                      style={{
                                        backgroundColor: currentPage === page ? COLORS.primary : 'transparent',
                                        borderColor: currentPage === page ? COLORS.primary : '#e0e0e0',
                                        color: currentPage === page ? COLORS.white : COLORS.primary,
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        margin: '0 2px'
                                      }}
                                      data-bs-toggle="tooltip"
                                      data-bs-placement="bottom"
                                      data-bs-title={`Page ${page}`}
                                    >
                                      {page}
                                    </button>
                                  </li>
                                );
                              })}
                            
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                              <button 
                                className="page-link fw-semibold"
                                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                                disabled={currentPage === totalPages}
                                style={{
                                  color: currentPage === totalPages ? '#B0BEC5' : COLORS.primary,
                                  borderColor: '#e0e0e0',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  margin: '0 2px'
                                }}
                                data-bs-toggle="tooltip"
                                data-bs-placement="bottom"
                                data-bs-title="Page suivante"
                              >
                                <i className="bi bi-chevron-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .page-link,
        th {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .table,
        td,
        small,
        .alert {
          font-family: 'Roboto', sans-serif;
        }
        
        .btn:focus,
        .form-control:focus,
        .page-link:focus,
        .form-select:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn:disabled,
        .page-link:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:focus,
        .form-select:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .table-hover tbody tr:hover {
          background-color: ${COLORS.lightBg};
          transition: background-color 0.2s ease;
        }
        
        .badge {
          font-weight: 500;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .bi-arrow-clockwise.spin {
          animation: spin 1s linear infinite;
        }
        
        /* Style des tooltips Bootstrap */
        .tooltip {
          font-family: 'Roboto', sans-serif;
          font-size: 0.85rem;
        }
        
        .tooltip-inner {
          background-color: ${COLORS.primary};
          color: white;
          border-radius: 6px;
          padding: 6px 12px;
          max-width: 300px;
        }
        
        .tooltip-arrow {
          display: none;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .table-responsive {
            border: none;
          }
          
          .table {
            font-size: 0.875rem;
          }
          
          .table th,
          .table td {
            padding: 12px 8px !important;
          }
          
          .row.g-3 {
            margin-bottom: 8px;
          }
          
          .row.g-3 > div {
            margin-bottom: 8px;
          }
        }
        
        .table th {
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-size: 0.85rem;
        }
        
        .table td {
          vertical-align: middle;
        }
        
        .table-hover tbody tr {
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .table-hover tbody tr:hover {
          background-color: #f0f7ff;
        }
        
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        
        ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
    </Layout>
  );
};

export default ClientList;